<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="detailedForm">
      <v-row>
        <!-- Description -->
        <v-col cols="12">
          <label>{{ $t('description') }}</label>
          <p class="label-info">
            A high-level description of the Hosting Institution in fairly non-technical terms, with
            the vision, mission, objectives, background, experience.
          </p>
          <tiptap-vuetify
            v-model="hosting.description"
            :placeholder="'Max length is 2000 characters'"
            :extensions="extensions"
            class="required"
            :rules="requiredRule()"
          ></tiptap-vuetify>
          <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
            {{ charCount }} / {{ maxChars }} characters
          </div>
        </v-col>
      </v-row>
      <!-- Social Media -->
      <v-row>
        <v-col cols="12">
          <label>{{ $t('social-media') }}</label>
          <p class="label-info">
            Link to the logo/visual identity of the Hosting Institution or if not available of the
            Hosting Institution
          </p>
          <v-row v-for="(socialMedia, i) in hosting.socialMedia" :key="i">
            <v-col cols="10" xl="5" lg="5" md="5" sm="10" class="pb-0">
              <v-text-field
                v-model="socialMedia.url"
                :placeholder="$t('paste-url')"
                outlined
                :rules="requiredRule() && checkURL()"
                class="required"
                clearable
                prepend-inner-icon="mdi-link-variant"
              ></v-text-field>
            </v-col>
            <v-col cols="10" xl="5" lg="5" md="5" sm="10" class="pb-0">
              <v-select
                v-model="socialMedia.type"
                :items="['Twitter', 'Facebook', 'Instagram', 'LinkedIn', 'Youtube', 'Other']"
                outlined
              ></v-select>
            </v-col>
            <v-col cols="2" v-if="hosting.socialMedia.length > 1">
              <v-btn fab small text @click="removeSocialLink(i)">
                <v-icon>
                  $vuetify.icons.trash
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                @click="addSocialLink()"
                depressed
                rounded
                large
                color="primary200"
                class="white--text font-weight-bold"
              >
                <v-icon left>
                  $vuetify.icons.plus
                </v-icon>
                <span class="text-decoration-underline text-transform-none font-weight-bold">
                  {{ $t('add-social-link') }}
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <!-- Logo -->
      <v-row>
        <v-col cols="10" xl="5" lg="5" md="5" sm="10">
          <label>Logo</label>
          <p class="label-info">
            Link to the logo/visual identity of the Hosting Institution or if not available of the
            Hosting Institution
          </p>
          <v-text-field
            v-model.trim="hosting.logoUrl"
            :rules="requiredRule()"
            class="required"
            outlined
            :placeholder="'Paste a URL'"
            prepend-inner-icon="mdi-link-variant"
            clearable
            @input="handleLogoInput"
          >
          </v-text-field>
          <v-alert v-if="!isValidLogoUrl" type="error" class="subtitle-2 py-2">
            Please enter a valid URL
          </v-alert>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="unsavedChanges && edit"
          @click="updateHostingDetailedInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Strike,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'AddHostingDetailedInfo',
  components: { TiptapVuetify, AlertUnsavedChanges },
  mixins: [util],
  data: () => ({
    hosting: {
      description: '',
      logoUrl: '',
      socialMedia: [
        {
          url: '',
          type: 'Twitter',
        },
      ],
    },
    unsavedChanges: false,
    dataLoaded: false,
    initialHosting: {},
    initialSocialMedia: {},
    isValidLogoUrl: true,
    extensions: [Bold, Italic, Underline, Strike, ListItem, BulletList, OrderedList],
    maxChars: 2000,
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    hosting: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.hosting.description !== this.initialHosting.description ||
          this.hosting.logoUrl !== this.initialHosting.logoUrl;
      },
    },
    'hosting.socialMedia': {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialSocialMedia)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
  },
  mounted() {
    if (this.edit) {
      this.getHosting();
    }
  },
  computed: {
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.hosting.description || '';
      return div.textContent.length;
    },
  },
  methods: {
    back() {
      this.$emit('tab', 0);
    },
    stripHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    },
    next() {
      if (!this.hosting.description || this.stripHtml(this.hosting.description).trim() === '') {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Description Required'),
          color: 'error',
        });
        return;
      }

      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid && this.hosting.description) {
        this.$emit('tab', 2);
        this.$emit('hostingDetailedInfo', { ...this.hosting });
      }
    },
    handleLogoInput(value) {
      if (value) {
        this.validateLogoUrl(value);
      } else {
        this.isValidLogoUrl = true;
      }
    },
    validateLogoUrl() {
      this.isValidLogoUrl = this.canParse(this.hosting.logoUrl);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
    removeSocialLink(index) {
      this.hosting.socialMedia.splice(index, 1);
    },
    addSocialLink() {
      this.hosting.socialMedia.push({
        url: '',
        type: 'Twitter',
      });
    },
    getHosting() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/hosting-institutions/' + id)
        .then(res => {
          console.log('Get Hosting Institution Detailed Info', res.data);

          this.hosting.description = res.data.description;
          this.hosting.logoUrl = res.data.logoUrl;

          if (res.data.socialMedia) {
            this.hosting.socialMedia = res.data.socialMedia.map(media => ({
              type: media.type,
              url: media.url,
            }));

            this.initialSocialMedia = JSON.parse(JSON.stringify(this.hosting.socialMedia));
          }

          this.initialHosting = {
            description: res.data.description,
            logoUrl: res.data.logoUrl,
          };

          this.dataLoaded = true;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.getHosting();
    },
    updateHostingDetailedInfo() {
      if (
        !this.hosting.description ||
        this.stripHtml(this.hosting.description).trim() === '' ||
        !this.hosting.logoUrl ||
        !this.hosting.socialMedia.some(media => media.url && media.url.trim() !== '')
      ) {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Field Required'),
          color: 'error',
        });
        return;
      }

      const id = this.$route.params.id;

      const hostingUpdateData = {
        id: id,
        description: this.hosting.description,
        logoUrl: this.hosting.logoUrl,
        socialMedia: this.hosting.socialMedia,
      };

      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid) {
        this.axios
          .post('/api/v1/hosting-institutions/detailed-info', hostingUpdateData)
          .then(response => {
            console.log('Hosting Institution info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Hosting Institution Updated Successfully',
              color: 'primary200',
            });
            this.initialHosting = { ...this.hosting };
            this.getHosting();
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating Hosting Institution info:', error);
          });
      }
    },
  },
};
</script>
